import React from "react";

const FakeOrderShieldPrivacyPolicy = () => {
  return (
    <div className="container mx-auto px-4 py-40">
      <h1 className="text-3xl font-bold mb-6">
        Privacy Policy for Fake Order Shield
      </h1>

      <p className="mb-2">
        <strong>Effective Date:</strong> August 5, 2026
      </p>
      <p className="mb-6">
        <strong>Last Updated:</strong> August 5, 2026
      </p>

      <p className="mb-6">
        Fake Order Shield is provided by <b>Mean3 Pvt LTD</b> ("Mean3," "we," "us," or
        "our"). This Privacy Policy explains how we collect, process, use,
        store, and disclose information when a merchant installs or uses the
        Fake Order Shield application.
      </p>

      <p className="mb-6">
        Fake Order Shield helps merchants manage repeat fake or suspicious
        orders using email addresses and IP addresses added to a
        merchant-controlled blocklist. Based on the merchant's selected
        settings, the app may cancel, tag, and archive matching orders
        automatically. Merchants may also select and process multiple
        suspicious orders through a bulk action.
      </p>

      <p className="mb-6">
        By installing or using Fake Order Shield, merchants acknowledge the
        practices described in this Privacy Policy.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        1. Information We Process
      </h2>
      <p className="mb-2">
        We process information that is reasonably necessary to operate and
        support Fake Order Shield.
      </p>

      <h3 className="text-xl font-semibold mb-2">
        Merchant and Store Information
      </h3>
      <p className="mb-2">
        When a merchant installs or uses the app, we may process:
      </p>
      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Store name</li>
        <li>Store domain</li>
        <li>Store identifier</li>
        <li>App installation and authentication information</li>
        <li>App settings and blocking preferences</li>
        <li>Information provided through support requests</li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">
        Blocked Entry Information
      </h3>
      <p className="mb-2">
        Merchants can create and manage blocked entries that may contain:
      </p>
      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Email addresses</li>
        <li>IP addresses</li>
        <li>Active or inactive status</li>
        <li>Selected blocking method</li>
        <li>Entry creation and update information</li>
        <li>Number of orders associated with an entry</li>
      </ul>
      <p className="mb-4">
        Blocked entries are created, managed, and controlled by the merchant.
      </p>

      <h3 className="text-xl font-semibold mb-2">Order Information</h3>
      <p className="mb-2">
        When checking or processing an order, the app may receive information
        made available through Shopify's APIs, webhooks, or admin extensions,
        including:
      </p>
      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Order ID</li>
        <li>Order number</li>
        <li>Customer email address</li>
        <li>Customer IP address, when available</li>
        <li>Order status</li>
        <li>Order tags</li>
        <li>Order creation information</li>
        <li>Cancellation status</li>
        <li>Archive status</li>
        <li>Information required to identify whether an order matches a blocked entry</li>
      </ul>
      <p className="mb-4">
        The app may also process information relating to orders selected by
        the merchant through the bulk order blocking action.
      </p>

      <h3 className="text-xl font-semibold mb-2">Technical Information</h3>
      <p className="mb-2">
        We may process technical information required to operate, maintain,
        and secure the app, including:
      </p>
      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Webhook processing information</li>
        <li>Error logs</li>
        <li>App activity logs</li>
        <li>Action dates and times</li>
        <li>Authentication information</li>
        <li>Security and performance information</li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">
        2. How We Use Information
      </h2>
      <p className="mb-2">We may use information to:</p>
      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Provide and operate Fake Order Shield</li>
        <li>Check new orders against active blocked entries</li>
        <li>Match orders using email addresses, IP addresses, or both</li>
        <li>Cancel matching orders</li>
        <li>Add app-related tags to matching orders</li>
        <li>Archive matching orders</li>
        <li>Process merchant-selected orders through bulk actions</li>
        <li>Display and manage blocked entries</li>
        <li>Display matched order counts</li>
        <li>Authenticate merchants and authorized staff</li>
        <li>Provide customer and technical support</li>
        <li>Investigate errors and failed actions</li>
        <li>Protect the app against misuse or unauthorized access</li>
        <li>Maintain and improve app performance</li>
        <li>Respond to privacy requests and complaints</li>
        <li>Comply with applicable legal and platform requirements</li>
      </ul>
      <p className="mb-2">
        We do not use customer email addresses or IP addresses for
        independent advertising or marketing.
      </p>
      <p className="mb-6">
        We do not sell personal information processed through Fake Order
        Shield.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        3. Automated Order Actions
      </h2>
      <p className="mb-2">
        Fake Order Shield may automatically take action when an order matches
        an active blocked entry according to the settings selected by the
        merchant.
      </p>
      <p className="mb-2">These actions may include:</p>
      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Cancelling the order</li>
        <li>Applying app-related tags</li>
        <li>Archiving the order</li>
        <li>Recording that the order matched a blocked entry</li>
      </ul>
      <p className="mb-2">
        The app checks orders after they are created. It does not prevent
        customers from reaching or completing the checkout before an order is
        created.
      </p>
      <p className="mb-2">
        Fake Order Shield does not independently determine that a customer
        has committed fraud. Actions are based on the blocklist, matching
        method, and settings configured by the merchant.
      </p>
      <p className="mb-2">
        Merchants are responsible for maintaining accurate blocked entries and
        reviewing their blocking settings.
      </p>
      <p className="mb-6">
        Different customers may share the same IP address through households,
        offices, public Wi-Fi networks, mobile networks, or VPN services.
        Merchants should consider this before using IP-only blocking.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        4. How We Share Information
      </h2>
      <p className="mb-4">
        We may share information only where reasonably necessary to provide
        the app or meet legal obligations.
      </p>

      <h3 className="text-xl font-semibold mb-2">Shopify</h3>
      <p className="mb-4">
        Fake Order Shield exchanges information with Shopify through
        authorized APIs, webhooks, and admin extensions to provide its
        functionality.
      </p>

      <h3 className="text-xl font-semibold mb-2">
        Hosting and Database Services
      </h3>
      <p className="mb-4">
        The app's hosting and database infrastructure is provided through
        Namecheap. Namecheap may process technical or stored information as
        required to provide hosting and infrastructure services.
      </p>

      <h3 className="text-xl font-semibold mb-2">Legal Requirements</h3>
      <p className="mb-2">
        We may disclose information when reasonably necessary to:
      </p>
      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Comply with applicable laws, regulations, court orders, or legal requests</li>
        <li>Protect our legal rights</li>
        <li>Investigate fraud, misuse, or security incidents</li>
        <li>Protect merchants, customers, Mean3, or other individuals</li>
        <li>Enforce applicable agreements</li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">Business Transfers</h3>
      <p className="mb-2">
        Information may be transferred as part of a merger, acquisition,
        restructuring, financing, sale of assets, or similar business
        transaction.
      </p>
      <p className="mb-6">
        Any successor will be responsible for handling information in
        accordance with applicable privacy requirements.
      </p>

      <h2 className="text-2xl font-semibold mb-4">5. Data Retention</h2>
      <p className="mb-2">
        We retain information only for as long as reasonably necessary to:
      </p>
      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Provide Fake Order Shield</li>
        <li>Maintain app functionality</li>
        <li>Resolve technical issues</li>
        <li>Respond to disputes</li>
        <li>Enforce agreements</li>
        <li>Meet legal and platform requirements</li>
      </ul>
      <p className="mb-2">
        Blocked entries and related app settings may be retained while the
        app remains installed.
      </p>
      <p className="mb-6">
        When the app is uninstalled, applicable store and customer
        information will be deleted or anonymized according to our deletion
        procedures, except where retention is required for legal, security,
        accounting, or dispute-resolution purposes.
      </p>

      <h2 className="text-2xl font-semibold mb-4">6. Data Security</h2>
      <p className="mb-2">
        We use reasonable administrative, organizational, and technical
        safeguards designed to protect information against:
      </p>
      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Unauthorized access</li>
        <li>Accidental loss</li>
        <li>Improper disclosure</li>
        <li>Alteration</li>
        <li>Misuse</li>
        <li>Destruction</li>
      </ul>
      <p className="mb-2">
        No online service, electronic transmission, or storage system can be
        guaranteed to be completely secure.
      </p>
      <p className="mb-6">
        Merchants are responsible for protecting their Shopify accounts,
        staff permissions, passwords, and devices.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        7. Merchant Responsibilities
      </h2>
      <p className="mb-2">
        Merchants using Fake Order Shield are responsible for:
      </p>
      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Providing customers with an appropriate privacy notice</li>
        <li>Having an appropriate legal basis for processing customer information</li>
        <li>Using customer email addresses and IP addresses in accordance with applicable law</li>
        <li>Maintaining accurate blocked entries</li>
        <li>Regularly reviewing app settings and blocklists</li>
        <li>Limiting app access to authorized staff members</li>
        <li>Responding to customer questions and privacy requests</li>
        <li>Avoiding unlawful, discriminatory, or abusive use of the app</li>
      </ul>
      <p className="mb-6">
        Fake Order Shield processes information according to the merchant's
        configuration and instructions.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        8. Privacy Rights and Requests
      </h2>
      <p className="mb-2">
        Depending on applicable law, individuals may have the right to:
      </p>
      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Request access to their personal information</li>
        <li>Request correction of inaccurate information</li>
        <li>Request deletion of personal information</li>
        <li>Object to or restrict certain processing</li>
        <li>Request a portable copy of their information</li>
        <li>Submit a privacy complaint</li>
      </ul>
      <p className="mb-2">
        Customers should normally contact the merchant from whom they placed
        an order. The merchant controls the customer relationship and
        determines how customer order information is used.
      </p>
      <p className="mb-2">
        Merchants and customers may also contact Mean3 regarding information
        processed through Fake Order Shield.
      </p>
      <p className="mb-6">
        We may request information needed to verify the identity and
        authority of the person submitting a request.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        9. Privacy Complaints
      </h2>
      <p className="mb-2">
        Questions, privacy requests, and complaints regarding Fake Order
        Shield may be submitted to:
      </p>
      <p className="mb-2">
        <strong>Email:</strong>{" "}
        <a
          href="mailto:Support@mean3.com"
          className="text-blue-500 hover:underline"
        >
          Support@mean3.com
        </a>
      </p>
      <p className="mb-2">
        Please include the following information where applicable:
      </p>
      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Your full name</li>
        <li>Merchant store name</li>
        <li>Store domain</li>
        <li>Description of the request or complaint</li>
        <li>Order number, where relevant</li>
        <li>Any information that may help us review the matter</li>
      </ul>
      <p className="mb-2">
        We will review privacy complaints and respond within a reasonable
        period based on the nature and complexity of the request.
      </p>
      <p className="mb-6">
        Individuals may also have the right to submit a complaint to an
        applicable privacy or data protection authority.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        10. Cookies and Similar Technologies
      </h2>
      <p className="mb-2">
        Fake Order Shield may use essential cookies, sessions, or similar
        technologies required for authentication, security, and operation of
        the merchant-facing app.
      </p>
      <p className="mb-6">
        The app does not use customer order information to provide targeted
        advertising.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        11. Children's Information
      </h2>
      <p className="mb-2">
        Fake Order Shield is a business application intended for merchants
        and their authorized staff members. It is not directed toward
        children.
      </p>
      <p className="mb-6">
        We do not knowingly collect personal information directly from
        children through the app.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        12. Third-Party Services
      </h2>
      <p className="mb-2">
        Fake Order Shield operates in connection with Shopify and Namecheap.
      </p>
      <p className="mb-6">
        These third-party services maintain their own privacy policies and
        data-handling practices. Mean3 is not responsible for the independent
        privacy practices of third-party services.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        13. Changes to This Privacy Policy
      </h2>
      <p className="mb-2">We may update this Privacy Policy when:</p>
      <ul className="list-disc list-inside mb-4 ml-4">
        <li>App functionality changes</li>
        <li>Our data practices change</li>
        <li>Our service providers change</li>
        <li>Legal or platform requirements change</li>
      </ul>
      <p className="mb-2">
        When changes are made, we will update the "Last Updated" date
        displayed at the beginning of this policy.
      </p>
      <p className="mb-6">
        Material changes may also be communicated through the app, website,
        email, or another appropriate method.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        14. Contact Information
      </h2>
      <p className="mb-2">
        For privacy questions, support requests, data requests, or
        complaints, contact:
      </p>
      <ul className="list-disc list-inside mb-4 ml-4">
        <li>
          <strong>Mean3 Pvt LTD</strong>
        </li>
        <li>
          <strong>Email:</strong>{" "}
          <a
            href="mailto:Support@mean3.com"
            className="text-blue-500 hover:underline"
          >
            Support@mean3.com
          </a>
        </li>
        <li>
          <strong>Website:</strong>{" "}
          <a
            href="https://mean3.com"
            className="text-blue-500 hover:underline"
          >
            https://mean3.com
          </a>
        </li>
        <li>
          <strong>Address:</strong> 447 Broadway, 2nd Floor, Suite #670, New
          York, NY 10013, United States
        </li>
      </ul>
    </div>
  );
};

export default FakeOrderShieldPrivacyPolicy;